import GenericError from '../errors/GenericError';
import MetaField from './MetaField';

const MIN_ORDINAL = 0;
const MAX_ORDINAL = 1000;

const applyFilter = (filterObj) => (field) => {
  if (typeof filterObj !== 'object') {
    throw new GenericError('invalid-meta-override-filter', `Expected filter to be an object but was ${typeof value} instead.`);
  }

  const keys = Object.keys(filterObj);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const filter = filterObj[key];

    // If the field is a function, then pass the value into the function and let that be what is evaluated
    if (typeof filter === 'function') {
      if (!filter(field.attr(key))) {
        return false;
      }
    // Else just match the value of the field to the value of the filter obj
    } else if (field.attr(key) !== filterObj[key]) {
      return false;
    }
  }

  return true;
};

const applyOverride = (override) => (field) => {
  if (typeof override !== 'object') {
    throw new GenericError('invalid-meta-override-value', `Expected override to be an object but was ${typeof override} instead.`);
  }

  const keys = Object.keys(override);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const overrideValue = override[key];

    if (typeof overrideValue === 'function') {
      switch(key) {
        case 'options':
          // Options are special if we want to update them by function.
          // First export them so we are working with a standard JS object.
          const exported = field.export()[key];
          // Then run function which passes the options array into the override function
          const updated = overrideValue(exported);
          // Then create a new meta field which will get merged in with the existing field.
          const otherField = new MetaField({ data: { [key]: updated } });
          // Then let the merge process take over, which knows how to merge two fields properly.
          field.merge(otherField);
          break;
        default:
          field.attr(key, overrideValue(field.attr(key), field));
          break;
      }
    } else {
      field.attr(key, overrideValue)
    }
  }

  return field;
};

const applyOverrideFunction = (overrideFunc) => (field) => {
  const exportedField = field.export();
  // The user wants to do something more complex here. First export so they're working with a JS object
  // Create a new meta field out of their override
  const otherField = new MetaField({ data: overrideFunc(exportedField, { field }) });

  return field.merge(otherField);
};

class MetaOverride {
  constructor(filter, value, args = {}) {
    const {
      ordinal = 0,
    } = args;

    this._value = value;
    this._filterObj = filter;

    this._filter = (typeof filter === 'function') ? filter : applyFilter(filter);
    this._override = (typeof value === 'function') ? applyOverrideFunction(value) : applyOverride(value);

    if (typeof ordinal !== 'number') {
      throw new GenericError('invalid-ordinal-type', `ordinal must be a number. (received ${ordinal})`);
    } else if (ordinal < MIN_ORDINAL) {
      throw new GenericError('ordinal-too-low', `ordinal must be at least ${MIN_ORDINAL}`);
    } else if (ordinal > MAX_ORDINAL) {
      throw new GenericError('ordinal-too-high', `ordinal must be no greater than ${MAX_ORDINAL}`);
    }

    this._ordinal = args.ordinal || 0;
  }

  value() {
    return this._value;
  }

  matches(field, args) {
    return this._filter(field, args);
  }

  apply(field, args = {}) {
    const {
      shouldSkipMatch = false,
    } = args;

    if (shouldSkipMatch || this.matches(field, args)) {
      return this._override(field, args);
    }
  }

  ordinal() {
    return this._ordinal;
  }
}

export default MetaOverride;
